import { useTranslation } from 'next-i18next';
import { NextRouter, useRouter } from 'next/router';
import React from 'react';
import styled from 'styled-components';
import { TabControls } from '@hotelplan/components.common.tabs';
import { EPageType } from '@hotelplan/fdr.lib.page.available-pages';
import { targetPageData } from '@hotelplan/fdr.lib.router.target-page-data';
import { useDeviceType } from '@hotelplan/libs.context.device-type';
import { getRouteByPageTypeLocale } from '@hotelplan/libs.router-config';
import { FdrLinkType } from '@hotelplan/supergraph-api';
import { sx2CssThemeFn } from '@hotelplan/util.theme.sxc';
import FdrWishlistFiltersForm from 'fdr/components/local/fdr-wishlist/fdr-wishlist-filters-form';
import { WishlistViewType } from 'fdr/components/local/fdr-wishlist/fdr-wishlist.constants';
import FdrWishlistMapAccess from 'fdr/components/local/fdr-wishlist/map/fdr-wishlist-map-access';
import FdrWishlistFilterField from 'fdr/components/local/wishlist-filters/fdr-wishlist-filter-field';

export interface IWishlistSidebarProps {
  type: WishlistViewType;
}

const WishlistSidebarWrapper = styled.aside(
  sx2CssThemeFn({
    width: ['100%', `${100 / 3}%`],
    pr: [null, 3],
    mb: [null, 3],
  })
);

const TabControlsWrapper = styled.div(({ theme: { FONT_SIZE } }) =>
  sx2CssThemeFn({
    display: 'flex',
    '.tabControls': {
      height: ['47px', '54px'],
      fontSize: 2,
    },
    mb: 4,
    '.tab-label': {
      ...FONT_SIZE.TABS,
    },
  })
);

const onClick = (
  viewType: WishlistViewType,
  language: string,
  push: NextRouter['push']
) => {
  const route = getRouteByPageTypeLocale(
    viewType === WishlistViewType.Compare
      ? EPageType.WishlistCompare
      : EPageType.WishlistList,
    language
  );

  if (!route) return;

  const uri = route.builder({});

  const routerProps = targetPageData({
    __typename: 'FdrLink',
    url: uri,
    type: FdrLinkType.Internal,
  });

  push(route.page, uri, {
    shallow: false,
    scroll: false,
    locale: routerProps.locale,
  });
};

export const WishlistSidebar: React.FC<IWishlistSidebarProps> = props => {
  const { type } = props;
  const { push } = useRouter();
  const { mobile } = useDeviceType();
  const [t, { language }] = useTranslation('wishlist');

  return (
    <WishlistSidebarWrapper data-id="wishlist-sidebar">
      <TabControlsWrapper>
        <TabControls
          checked={type === WishlistViewType.List}
          onClick={() => onClick(WishlistViewType.List, language, push)}
        >
          {t('wishlist:listView.tab')}
        </TabControls>
        <TabControls
          checked={type === WishlistViewType.Compare}
          onClick={() => onClick(WishlistViewType.Compare, language, push)}
        >
          {t('wishlist:compare.tab')}
        </TabControls>
      </TabControlsWrapper>
      {!mobile && type === WishlistViewType.List && <FdrWishlistMapAccess />}
      {type === WishlistViewType.Compare && !mobile && (
        <FdrWishlistFiltersForm>
          <FdrWishlistFilterField />
        </FdrWishlistFiltersForm>
      )}
    </WishlistSidebarWrapper>
  );
};
